import Icon from '@ant-design/icons';
import { Tag } from 'antd';
import { useTranslate } from '@tolgee/react';
import { ReactComponent as TransferToHumanIcon } from 'views/PureLLM/assets/transfer-to-human.svg';
import { ReactComponent as NoReplyIcon } from 'views/PureLLM/assets/no-reply.svg';
import { ReactComponent as FixedReplyIcon } from 'views/PureLLM/assets/fixed-reply.svg';
import { ReactComponent as LogisticsCardIcon } from 'views/PureLLM/assets/logistics-card.svg';
import { ReactComponent as PlaceholderIcon } from 'views/PureLLM/assets/placeholder-icon.svg';
import { ReactComponent as TodoIcon } from 'views/PureLLM/assets/todo-icon.svg';
import type { ApiReceptionMode } from 'views/PureLLM/Types/api';

export type ReceptionModeTagKey =
  | 'transfer_human'
  | 'no_reply'
  | 'ai_reply'
  | 'logistics_card'
  | 'wait_buyer_question'
  | 'create_todo';

export type { ApiReceptionMode };

export interface ReceptionModeTagItem {
  key: ReceptionModeTagKey;
  label: string;
  icon: any;
  backgroundColor: string;
  borderColor: string;
  color: string;
}

export const RECEPTION_MODE_TAG_ITEMS: ReceptionModeTagItem[] = [
  {
    key: 'transfer_human',
    label: 'scene.aiAgentConfig.receptionMode.tag.transferToHuman',
    icon: TransferToHumanIcon,
    backgroundColor: 'var(--fett-bg-subtle)',
    borderColor: 'var(--fett-border-strong)',
    color: 'var(--fett-text-primary)',
  },
  {
    key: 'no_reply',
    label: 'scene.aiAgentConfig.receptionMode.tag.doNotReply',
    icon: NoReplyIcon,
    backgroundColor: 'var(--fett-bg-subtle)',
    borderColor: 'var(--fett-border-strong)',
    color: 'var(--fett-text-primary)',
  },
  {
    key: 'ai_reply',
    label: 'scene.aiAgentConfig.receptionMode.tag.aiReply',
    icon: FixedReplyIcon,
    backgroundColor: 'var(--fett-bg-subtle)',
    borderColor: 'var(--fett-border-strong)',
    color: 'var(--fett-text-primary)',
  },
  {
    key: 'logistics_card',
    label: 'scene.aiAgentConfig.receptionMode.tag.sendLogisticsCard',
    icon: LogisticsCardIcon,
    backgroundColor: 'var(--fett-bg-subtle)',
    borderColor: 'var(--fett-border-strong)',
    color: 'var(--fett-text-primary)',
  },
  {
    key: 'wait_buyer_question',
    label: 'scene.aiAgentConfig.receptionMode.tag.waitBuyerQuestion',
    icon: PlaceholderIcon,
    backgroundColor: 'var(--fett-bg-subtle)',
    borderColor: 'var(--fett-border-strong)',
    color: 'var(--fett-text-primary)',
  },
  {
    key: 'create_todo',
    label: 'scene.aiAgentConfig.receptionMode.tag.createTodo',
    icon: TodoIcon,
    backgroundColor: 'var(--fett-bg-subtle)',
    borderColor: 'var(--fett-border-strong)',
    color: 'var(--fett-text-primary)',
  },
];

export const RECEPTION_MODE_TAG_KEYS_BY_VALUE: Record<ApiReceptionMode, ReceptionModeTagKey[]> = {
  AI_REPLY: ['ai_reply'],
  AI_REPLY_AND_HUMAN: ['ai_reply', 'transfer_human'],
  AI_REPLY_AND_LOGISTICS: ['ai_reply', 'logistics_card'],
  AI_REPLY_AND_LOGISTICS_HUMAN: ['ai_reply', 'logistics_card', 'transfer_human'],
  AI_REPLY_AND_TODO: ['ai_reply', 'create_todo'],
  AI_REPLY_AND_HUMAN_TODO: ['ai_reply', 'transfer_human', 'create_todo'],
  NOT_REPLY: ['no_reply', 'transfer_human'],
  NOT_REPLY_AND_TODO: ['no_reply', 'transfer_human', 'create_todo'],
  NOT_REPLY_WAITING: ['no_reply', 'wait_buyer_question'],
  RM_UNKNOWN: ['ai_reply'],
};

export function ReceptionModeTags(props: {
  value: ApiReceptionMode;
  todoLabel?: string;
  className?: string;
}) {
  const { t } = useTranslate();
  const {
    value,
    todoLabel = t('scene.aiAgentConfig.receptionMode.tag.defaultTodoLabel'),
    className,
  } = props;
  const tagKeys = RECEPTION_MODE_TAG_KEYS_BY_VALUE[value];

  return (
    <div data-sid="rmt-root-01" className={className ?? 'flex flex-wrap gap-2'}>
      {tagKeys.map((key) => {
        const item = RECEPTION_MODE_TAG_ITEMS.find((tagItem) => tagItem.key === key);
        if (!item) return null;

        const tagBackgroundColor =
          key === 'ai_reply'
            ? 'var(--fett-surface-success-bg)'
            : key === 'transfer_human'
            ? 'var(--fett-surface-warning-bg)'
            : key === 'logistics_card'
            ? 'var(--fett-scenario-purple-bg)'
            : key === 'wait_buyer_question'
            ? 'var(--fett-scenario-orange-bg)'
            : key === 'create_todo'
            ? 'var(--fett-scenario-accent-bg)'
            : item.backgroundColor;

        const tagColor =
          key === 'ai_reply'
            ? 'var(--fett-scenario-success-text)'
            : key === 'transfer_human'
            ? 'var(--fett-surface-warning-text)'
            : key === 'logistics_card'
            ? 'var(--fett-scenario-purple-text)'
            : key === 'wait_buyer_question'
            ? 'var(--fett-scenario-orange-text)'
            : key === 'create_todo'
            ? 'var(--fett-scenario-accent-text)'
            : item.color;

        return (
          <Tag
            key={key}
            data-sid={`rmt-tag-${key}`}
            className="m-0 inline-flex box-border items-center whitespace-nowrap rounded-[4px] text-xs"
            style={{
              backgroundColor: tagBackgroundColor,
              border: 'none',
              color: tagColor,
              marginRight: 0,
              fontWeight: 400,
              fontSize: 14,
              height: 32,
              padding: '6px 12px',
              lineHeight: '20px',
            }}
          >
            <span
              data-sid={`rmt-content-${key}`}
              className="inline-flex items-center gap-1 leading-[20px]"
            >
              <Icon
                data-sid={`rmt-icon-${key}`}
                component={item.icon}
                style={{ fontSize: 16, color: tagColor }}
              />
              <span data-sid={`rmt-text-${key}`}>
                {key === 'create_todo'
                  ? t('scene.aiAgentConfig.receptionMode.tag.createTodoWithLabel', { todoLabel })
                  : t(item.label)}
              </span>
            </span>
          </Tag>
        );
      })}
    </div>
  );
}

export function ReceptionModeTagLegend() {
  const { t } = useTranslate();
  return (
    <div data-sid="rmt-legend-02" className="flex flex-wrap gap-2">
      {RECEPTION_MODE_TAG_ITEMS.map((item) => (
        <Tag
          key={item.key}
          data-sid={`rmt-legend-tag-${item.key}`}
          className="m-0 inline-flex box-border items-center whitespace-nowrap rounded-[4px] text-xs"
          style={{
            backgroundColor: item.backgroundColor,
            border: 'none',
            color: item.color,
            fontWeight: 400,
            fontSize: 14,
            height: 32,
            padding: '6px 12px',
            lineHeight: '20px',
          }}
        >
          <span
            data-sid={`rmt-legend-content-${item.key}`}
            className="inline-flex items-center gap-1 leading-[20px]"
          >
            <Icon
              data-sid={`rmt-legend-icon-${item.key}`}
              component={item.icon}
              style={{ fontSize: 16 }}
            />
            <span data-sid={`rmt-legend-text-${item.key}`}>{t(item.label)}</span>
          </span>
        </Tag>
      ))}
    </div>
  );
}
